import React from "react";
import { TranslateContext } from "../../context/TranslateContext";
import { GlobalContext } from "../../context/GlobalContext";
import { useParams } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { CreditCardOutlined, StarOutlined } from "@mui/icons-material";
import Header from "../components/Header";
import FooterComp from "../components/FooterComp";

import StripeComp from "./gateways/StripeComp";
import PayPalComp from "./gateways/PayPalComp";
import RazorpayComp from "./gateways/RazorpayComp";
import PaystackComp from "./gateways/PaystackComp";
import MercadoPagoComp from "./gateways/MercadoPagoComp";
import Free from "./gateways/Free";
import OfflineCheckout from "./gateways/OfflineCheckout"; // ← new

// ── maps gateway key → component ─────────────────────────────────────────────
const GATEWAY_COMPONENTS = {
  stripe: StripeComp,
  paypal: PayPalComp,
  razorpay: RazorpayComp,
  paystack: PaystackComp,
  mercadopago: MercadoPagoComp,
  free: Free,
};

const CheckOut = () => {
  const { lang } = React.useContext(TranslateContext);
  const { hitAxios } = React.useContext(GlobalContext);
  const { id } = useParams();
  const theme = useTheme();

  const hitAxiosRef = React.useRef(hitAxios);

  const [plan, setPlan] = React.useState(null);
  const [gateways, setGateways] = React.useState({});
  const [currency, setCurrency] = React.useState({
    symbol: "$",
    code: "USD",
    rate: 1,
  });
  const [offlineHtml, setOfflineHtml] = React.useState(""); // ← new
  const [offlineActive, setOfflineActive] = React.useState(false); // ← new
  const [paying, setPaying] = React.useState("");
  const [done, setDone] = React.useState(false);

  // ── fetch once when id is available ──────────────────────────────────────
  React.useEffect(() => {
    if (!id || done) return;
    setDone(true);

    (async () => {
      const [planRes, gwRes, currRes, offlineRes] = await Promise.all([
        hitAxiosRef.current({
          path: "/api/plan/get_plan_by_id",
          admin: false,
          post: true,
          obj: { id },
        }),
        hitAxiosRef.current({
          path: "/api/payment/active-gateways",
          admin: false,
          post: false,
        }),
        hitAxiosRef.current({
          path: "/api/payment/currency",
          admin: false,
          post: false,
        }),
        hitAxiosRef.current({
          // ← new
          path: "/api/payment/offline-details",
          admin: false,
          post: false,
        }),
      ]);

      if (planRes?.data?.success) setPlan(planRes.data.data);
      if (gwRes?.data?.success) setGateways(gwRes.data.data);
      if (currRes?.data?.success) setCurrency(currRes.data.data);
      if (offlineRes?.data?.success) {
        // ← new
        setOfflineHtml(offlineRes.data.html || "");
        setOfflineActive(!!offlineRes.data.active);
      }
    })();
  }, [id, done]);

  // ── only show gateways that are active AND have a component ready ─────────
  const activeGateways = Object.entries(gateways).filter(
    ([key, val]) => val.active && GATEWAY_COMPONENTS[key],
  );

  // ── currency conversion ───────────────────────────────────────────────────
  const localPrice = plan
    ? (parseFloat(plan.price) * currency.rate).toFixed(2)
    : "0.00";

  const localPriceStrike = plan?.price_strike
    ? (parseFloat(plan.price_strike) * currency.rate).toFixed(2)
    : null;

  const discount = localPriceStrike
    ? Math.round(((localPriceStrike - localPrice) / localPriceStrike) * 100)
    : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <Header />
      <Box sx={{ height: { xs: 56, md: 64 } }} />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {!plan?.id ? (
          <Box />
        ) : (
          <Box
            sx={{
              width: "100%",
              maxWidth: 480,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            {/* ── CARD HEADER ── */}
            <Box
              sx={{
                px: 3,
                py: 2.5,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <CreditCardOutlined sx={{ color: "primary.main" }} />
              <Typography fontWeight={700} variant="subtitle1">
                {lang?.checkout || "Checkout"}
              </Typography>
              {plan.popular === 1 && (
                <Chip
                  icon={<StarOutlined fontSize="inherit" />}
                  label={lang?.popular || "Popular"}
                  size="small"
                  color="primary"
                  sx={{ ml: "auto", fontWeight: 700, fontSize: "0.7rem" }}
                />
              )}
            </Box>

            <Box p={3}>
              <Stack spacing={3}>
                {/* ── PLAN SUMMARY ── */}
                <Stack spacing={1.5}>
                  <Typography variant="h5" fontWeight={800}>
                    {plan.title}
                  </Typography>

                  {/* price row */}
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    gap={1.5}
                    flexWrap="wrap"
                  >
                    <Typography variant="h4" fontWeight={800} color="primary">
                      {currency.symbol}
                      {localPrice}
                    </Typography>

                    {localPriceStrike && (
                      <Typography
                        variant="body1"
                        color="text.disabled"
                        sx={{ textDecoration: "line-through" }}
                      >
                        {currency.symbol}
                        {localPriceStrike}
                      </Typography>
                    )}

                    {discount && (
                      <Chip
                        label={`${discount}% OFF`}
                        size="small"
                        color="success"
                        sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                      />
                    )}
                  </Stack>

                  {/* currency info */}
                  <Typography variant="caption" color="text.disabled">
                    {lang?.priceIn || "Price in"} {currency.code.toUpperCase()}
                    {currency.rate !== 1 &&
                      ` · 1 USD = ${currency.rate} ${currency.code.toUpperCase()}`}
                  </Typography>

                  <Divider />

                  {/* plan features */}
                  {[
                    {
                      label: lang?.credits || "Credits",
                      value: Number(plan.credits).toLocaleString(),
                    },
                    {
                      label: lang?.maxChars || "Max Characters",
                      value: Number(plan.max_characters).toLocaleString(),
                    },
                    {
                      label: lang?.validity || "Validity",
                      value: `${plan.expiry_days} ${lang?.days || "days"}`,
                    },
                  ].map(({ label, value }) => (
                    <Stack
                      key={label}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {value}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Divider />

                {/* ── PAYMENT METHODS ── */}
                <Stack spacing={1.5}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={1}
                    textTransform="uppercase"
                    color="text.secondary"
                  >
                    {lang?.selectPayment || "Select Payment Method"}
                  </Typography>

                  {activeGateways.length === 0 && !offlineActive && (
                    <Typography variant="body2" color="text.secondary">
                      {lang?.noGateways ||
                        "No payment methods available at the moment."}
                    </Typography>
                  )}

                  {plan?.price < 1 ? (
                    <Free
                      plan={plan}
                      currency={currency}
                      paying={paying}
                      setPaying={setPaying}
                      lang={lang}
                    />
                  ) : (
                    <>
                      {activeGateways.map(([key, gwData]) => {
                        const GatewayComp = GATEWAY_COMPONENTS[key];
                        return (
                          <GatewayComp
                            key={key}
                            plan={plan}
                            gwData={gwData}
                            currency={currency}
                            paying={paying}
                            setPaying={setPaying}
                          />
                        );
                      })}

                      {/* ── OFFLINE PAYMENT ── */}
                      {offlineActive && offlineHtml && (
                        <OfflineCheckout
                          plan={plan}
                          currency={currency}
                          html={offlineHtml}
                          paying={paying}
                          setPaying={setPaying}
                          lang={lang}
                        />
                      )}
                    </>
                  )}
                </Stack>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>

      <FooterComp />
    </Box>
  );
};

export default CheckOut;
